// Profile sections. Only what an ORCA account has: its id and the site's
// display time zone, sign-in identities (linked or not), Web sessions and
// sign-out. Changes go through profileApi; the UI shows pending/error states.
import { useState, type ReactNode } from 'react';
import { useToast } from '@/app/providers/Toast';
import { XButton } from '@/components/ui/Buttons';
import { Icon } from '@/components/ui/Icon';
import { useProfileAction } from '@/hooks/queries';
import { profileApi } from '@/lib/api/client';
import { cx } from '@/lib/format';
import { timezones } from '@/lib/timezones';
import { useT } from '@/i18n';
import { useTime } from '@/hooks/useTime';
import { tzShort } from '@/lib/time';
import type { Profile, Session } from '@/types';
import { SelectField } from './Fields';
import { useAuth } from '@/features/auth/AuthProvider';

const BOT = 'Orcafamily_bot';

/** Small pill button used for row actions. */
export const Act = ({ children, onClick, danger, busy, disabled, type = 'button' }: { children: ReactNode; onClick?: () => void; danger?: boolean; busy?: boolean; disabled?: boolean; type?: 'button' | 'submit' }) => (
  <button type={type} className={cx('pf-act', danger && 'danger', busy && 'busy')} onClick={onClick} disabled={disabled || busy}>{busy ? <span className="pf-spin" /> : null}{children}</button>
);

const Status = ({ ok, children }: { ok: boolean; children: ReactNode }) => <span className={cx('pf-st', ok ? 'ok' : 'warn')}><i />{children}</span>;

/* ---------- the account ---------- */
export function AccountSection({ p }: { p: Profile }) {
  const { t } = useT('profile');
  const tf = useTime();
  const toast = useToast();
  const [tz, setTz] = useState(p.timezone);
  const save = useProfileAction(profileApi.setTimezone);
  const dirty = tz !== p.timezone;
  return (
    <div className="pf-form">
      <div className="pf-me">
        <span className="pf-av"><Icon name="user" /></span>
        <div><b>{t('account.id')}</b><small><code>{p.accountId}</code>{p.createdAt ? ` · ${t('account.since', { date: tf.dateLong(p.createdAt) })}` : ''}</small></div>
      </div>
      <p className="pf-hint">{t('account.idHint')}</p>
      <SelectField label={t('personal.tz')} value={tz} onChange={setTz} options={timezones().map(([v, c]) => [v, `${c} · ${tzShort(v)}`])} hint={t('personal.tzHint')} />
      <div className="pf-foot">
        {dirty && <Act onClick={() => setTz(p.timezone)}>{t('common:action.cancel')}</Act>}
        <XButton label={save.pending ? t('saving') : t('common:action.save')} inv sm busy={save.pending} className={cx(!dirty && 'off')}
          onClick={async () => { if (!dirty) return; await save.run(tz); toast(t('personal.saved')); }} />
      </div>
    </div>
  );
}

/* ---------- sign-in methods: status only (no provider ids, no linking from here) ---------- */
function MethodRow({ icon, title, text, linked }: { icon: string; title: string; text: string; linked: boolean }) {
  const { t } = useT('profile');
  return (
    <div className="prow">
      <div className="prow-h">
        <span className={cx('ic', icon === 'tg' && 'tg')}><Icon name={icon} /></span>
        <div className="sr-t"><b>{title}</b><small>{text}</small></div>
        <Status ok={linked}>{linked ? t('signin.linked') : t('signin.notLinked')}</Status>
        <span />
      </div>
    </div>
  );
}

export function SignInSection({ p }: { p: Profile }) {
  const { t } = useT('profile');
  const tf = useTime();
  const since = (v: string | null | undefined) => (v ? t('signin.since', { date: tf.dateLong(v) }) : '');
  return (
    <div className="prows">
      <MethodRow icon="mail" title={t('mail.title')} linked={!!p.email?.verified}
        text={p.email ? `${p.email.address} · ${p.email.verified ? t('mail.verified') : t('mail.unverified')}` : t('signin.mailNone')} />
      <MethodRow icon="globe" title="Google" linked={!!p.google} text={p.google ? since(p.google.linkedAt) || t('signin.googleOn') : t('signin.googleNone')} />
      <MethodRow icon="tg" title="Telegram" linked={!!p.telegram}
        text={p.telegram ? [t('signin.tgOn', { bot: BOT }), since(p.telegram.linkedAt)].filter(Boolean).join(' · ') : t('signin.tgNone')} />
      {/* Web and Telegram join only by an explicit link (Plus/Premium), never automatically */}
      {!p.telegram && <p className="note">{t('signin.separate')}</p>}
    </div>
  );
}

/* ---------- sessions ---------- */
export function SessionsSection({ p }: { p: Profile }) {
  const { t } = useT('profile');
  const tf = useTime();
  const toast = useToast();
  const end = useProfileAction(profileApi.endSession);
  const [ending, setEnding] = useState<string | null>(null);
  const run = async (s: Session) => {
    setEnding(s.id);
    try { await end.run(s.id); toast(t('ses.endedWeb')); } catch (e) { toast((e as Error).message); } finally { setEnding(null); }
  };
  return (
    <div className="pf-sessions">
      {p.sessions.map((s) => (
        <div key={s.id} className={cx('pf-ses', s.current && 'cur')}>
          <span className="ic"><Icon name="laptop" /></span>
          <div className="sr-t">
            <b>{t('ses.web')}{s.current && <em>{t('ses.this')}</em>}</b>
            <small>{[s.createdAt ? t('ses.started', { when: tf.when(s.createdAt) }) : '', s.expiresAt ? t('ses.until', { date: tf.dateLong(s.expiresAt) }) : ''].filter(Boolean).join(' · ')}</small>
          </div>
          {!s.current && <Act busy={ending === s.id} onClick={() => run(s)}>{t('ses.end')}</Act>}
        </div>
      ))}
      {p.sessions.length <= 1 && <p className="note">{t('ses.none')}</p>}
      <p className="pf-hint">{t('ses.privacy')}</p>
    </div>
  );
}

/* ---------- sign-out ---------- */
export function LogoutSection() {
  const { t } = useT('profile');
  const auth = useAuth();
  const [loggingOut, setLoggingOut] = useState(false);
  const [logoutError, setLogoutError] = useState(false);
  return (
    <div className="pf-danger">
      <div className="prow-h">
        <span className="ic"><Icon name="logout" /></span>
        <div className="sr-t"><b>{t('acc.logout')}</b><small>{t('acc.logoutText')}</small></div>
        <span />
        <Act busy={loggingOut} onClick={async () => {
          setLoggingOut(true); setLogoutError(false);
          try { await auth.logout(); }
          catch { setLogoutError(true); setLoggingOut(false); }
        }}>{t('acc.logoutBtn')}</Act>
      </div>
      {logoutError ? <p className="pf-err" role="alert">{t('auth:error.offline')}</p> : null}
    </div>
  );
}
